import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section from '../../common/Section'
import Alert from '../../common/Alert'
import { useLocale } from '../../../i18n/useLocale'

export default function Payments() {
  const { brand, support, payment } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Billing & Payments"
        description={`Understand the ${brand.name} billing model for B2C partners.`}
        breadcrumbs={[
          { label: 'Guides', path: '/guides' },
          { label: 'Billing & Payments' },
        ]}
      />

      <Section title="Payment Model" divider={false}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {brand.name} supports two payment models depending on partner type:
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Chip label="B2C Partners" color="primary" size="small" sx={{ mb: 1 }} />
              <Typography variant="h6">Post-paid (Monthly Invoicing)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                B2C partners receive monthly invoices for all parcel services used.
                No real-time payment integration is required - simply create parcels and
                generate labels. Billing is handled separately.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: '1 1 300px', opacity: 0.7 }}>
            <CardContent>
              <Chip label="C2C / Small Customers" size="small" variant="outlined" sx={{ mb: 1 }} />
              <Typography variant="h6">Pre-paid ({payment.primary})</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Individual customers and smaller partners pay per parcel via{' '}
                {payment.primary} through the Customer Portal. This is not relevant for
                API-integrated B2C partners.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Section>

      <Section title="How Post-paid Billing Works">
        <Typography variant="body1" sx={{ mb: 2 }}>
          As a B2C partner, your billing workflow is:
        </Typography>

        <Box component="ol" sx={{ pl: 3, '& li': { mb: 2 } }}>
          <li>
            <strong>Create parcels and ship</strong> - Use the Parcel API and Label API as normal
            throughout the billing period.
          </li>
          <li>
            <strong>Services are tracked automatically</strong> - Each parcel creation records
            the applicable services (sending fee, COD fee, etc.) against your account.
          </li>
          <li>
            <strong>Monthly invoice</strong> - At the end of the billing period, {brand.name} generates
            an invoice with all billable items, grouped by service type.
          </li>
          <li>
            <strong>Payment</strong> - Settle the invoice according to your agreed payment terms.
          </li>
        </Box>
      </Section>

      <Section title="Billable Services">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The following services are tracked for billing:
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Chip label="SENDING_FEE" color="primary" />
          <Chip label="COD_FEE" color="secondary" />
          <Chip label="TRANSACTION_FEE" />
        </Box>

        <Typography variant="body2" color="text.secondary">
          Pricing is determined by your partner agreement — contact your account manager for your
          rate card. B2C partners with volume agreements may have custom pricing tiers.
        </Typography>
      </Section>

      <Section title="Cash on Delivery (COD)">
        <Typography variant="body1" sx={{ mb: 2 }}>
          For parcels with Cash on Delivery, the recipient pays the COD amount{' '}
          <strong>before collection</strong> — after the parcel arrives at the APM they receive a
          payment link, and the collection code is released once payment is confirmed. The COD
          amount is then settled with you as part of the billing cycle.
        </Typography>

        <Alert type="info" title="COD Limits">
          COD amounts are limited by default. Set the{' '}
          <code>codAmount</code> field when creating parcels.
        </Alert>
      </Section>

      <Section title="No API Integration Required">
        <Alert type="success" title="For B2C Partners">
          As a post-paid B2C partner, you do not need to integrate any payment APIs.
          Focus on the core integration: parcel creation, label generation, and tracking.
          Billing is managed by your {brand.name} account manager.
        </Alert>

        <Typography variant="body1" sx={{ mt: 2 }}>
          If you have questions about your billing or need access to invoice data,
          contact your {brand.name} account manager or email{' '}
          <a href={`mailto:${support.salesEmail}`}>{support.salesEmail}</a>.
        </Typography>
      </Section>
    </Box>
  )
}
